// Input parsing for the NRC Decoder. Accepts either a bare NRC byte or a full 3-byte
// UDS negative response (7F <SID> <NRC>), in a range of common hex spellings, and
// normalizes them into one shape for the UI to render — kept separate from the
// reference data in constants.js so the parsing rules can evolve independently.

const HEX_BYTE_RE = /^[0-9a-f]{1,2}$/i;

// Splits free-form input into byte tokens. Accepts space/comma-separated bytes
// (with or without individual "0x" prefixes), or one contiguous hex string of even
// length (e.g. "7F2231") which is split into byte pairs.
const tokenizeBytes = (raw) => {
  const cleaned = raw.trim();
  if (!cleaned) return { bytes: null, error: 'Enter an NRC or a UDS negative response.' };

  const withoutPrefixes = cleaned.replace(/0x/gi, ' ');
  const parts = withoutPrefixes.split(/[\s,;:_-]+/).filter(Boolean);

  let tokens = parts;
  // A single contiguous run of hex digits (no separators) — split into byte pairs.
  if (parts.length === 1 && /^[0-9a-f]+$/i.test(parts[0]) && parts[0].length > 2) {
    if (parts[0].length % 2 !== 0) {
      return { bytes: null, error: `"${cleaned}" has an odd number of hex digits (${parts[0].length}) — bytes must be 2 hex digits each.` };
    }
    tokens = parts[0].match(/.{2}/g);
  }

  const bytes = [];
  for (const t of tokens) {
    if (!HEX_BYTE_RE.test(t)) {
      return { bytes: null, error: `"${t}" is not a valid hex byte. Use hex digits 0-9/A-F only, e.g. "31" or "0x31".` };
    }
    bytes.push(parseInt(t, 16));
  }
  return { bytes, error: null };
};

// Returns a normalized result describing the input:
//   { ok: true, kind: 'nrc', nrcCode, bytes }
//   { ok: true, kind: 'response', sidCode, nrcCode, bytes }
//   { ok: false, error }
export const parseNrcInput = (raw) => {
  const { bytes, error } = tokenizeBytes(raw);
  if (error) return { ok: false, error };

  if (bytes.length === 1) {
    return { ok: true, kind: 'nrc', nrcCode: bytes[0], bytes };
  }

  if (bytes.length === 2) {
    if (bytes[0] === 0x7f) {
      return { ok: false, error: 'Incomplete negative response — a full response needs 3 bytes: 7F <Service ID> <NRC>. Only 2 bytes were given.' };
    }
    return { ok: false, error: `Got 2 bytes (${bytes.map((b) => `0x${b.toString(16).toUpperCase().padStart(2, '0')}`).join(' ')}). Enter either a single NRC byte, or a full 3-byte negative response starting with 7F.` };
  }

  if (bytes.length === 3) {
    if (bytes[0] !== 0x7f) {
      return { ok: false, error: `First byte 0x${bytes[0].toString(16).toUpperCase().padStart(2, '0')} is not 0x7F (the negative response indicator). A full negative response must start with 7F.` };
    }
    return { ok: true, kind: 'response', sidCode: bytes[1], nrcCode: bytes[2], bytes };
  }

  return { ok: false, error: `Got ${bytes.length} bytes. Enter either a single NRC byte (e.g. "31"), or a full 3-byte negative response (e.g. "7F 22 31").` };
};

export const EXAMPLE_INPUTS = ['0x22', '22', '7F 22 31', '7F 27 35'];
